import { useCallback } from 'react';
import { cancelLlmMode, cancelAnalysis } from '../services/api';

export const INITIAL_REVEALED_ITEMS = { motivations: [], barriers: [], next_steps: [] };
export const INITIAL_STAGE_METRICS = {
  stage: null,
  change_score_floor: null,
  change_score: null,
  refusal_count: null,
  client_type: null,
};

function useSessionTransition({
  sessionActive,
  sessionId,
  conversationStarted,
  showConfirm,
  completedFullSessionViewActive = false,
  feedbackPdfDownloaded = false,
  setSelectedProfile,
  setSessionActive,
  setSessionId,
  setConversationStarted,
  setSystemMessagesHistory,
  setViewedTurnIndex,
  setRevealedItems,
  setStageMetrics,
}) {
  const hasSessionData = Boolean(sessionActive || sessionId);

  const resetConversationState = useCallback(() => {
    setSessionId(null);
    setConversationStarted(false);
    setSystemMessagesHistory([]);
    setViewedTurnIndex(null);
    setRevealedItems(INITIAL_REVEALED_ITEMS);
    setStageMetrics(INITIAL_STAGE_METRICS);
  }, [
    setConversationStarted,
    setRevealedItems,
    setSessionId,
    setStageMetrics,
    setSystemMessagesHistory,
    setViewedTurnIndex,
  ]);

  const clearCurrentSessionData = useCallback(() => {
    setSessionActive(false);
    resetConversationState();
  }, [resetConversationState, setSessionActive]);

  const clearSessionSelection = useCallback(() => {
    setSelectedProfile(null);
    clearCurrentSessionData();
  }, [clearCurrentSessionData, setSelectedProfile]);

  const startSelectedProfileSession = useCallback((nextSelectedProfile) => {
    setSelectedProfile(nextSelectedProfile);
    setSessionActive(true);
    resetConversationState();
  }, [resetConversationState, setSelectedProfile, setSessionActive]);

  const cancelRunningSessionWork = useCallback(() => {
    if (sessionActive) {
      cancelLlmMode().catch((err) => console.error('Error cancelling LLM mode:', err));
      return;
    }

    if (sessionId) {
      cancelAnalysis().catch((err) => console.error('Error cancelling analysis:', err));
    }
  }, [sessionActive, sessionId]);

  const confirmSessionTransition = useCallback(async ({
    activeMessage,
    analysisMessage,
    completedMessage,
    completedUndownloadedFeedbackMessage,
    clearLocalStateWhenIdle = false,
  }) => {
    if (!hasSessionData) {
      return true;
    }

    if (!conversationStarted) {
      if (clearLocalStateWhenIdle) {
        clearCurrentSessionData();
      }
      return true;
    }

    const message = completedFullSessionViewActive
      ? (feedbackPdfDownloaded ? completedMessage : completedUndownloadedFeedbackMessage)
      : (sessionActive ? activeMessage : analysisMessage);
    const confirmed = await showConfirm(message);

    if (!confirmed) {
      return false;
    }

    cancelRunningSessionWork();
    return true;
  }, [
    cancelRunningSessionWork,
    clearCurrentSessionData,
    completedFullSessionViewActive,
    conversationStarted,
    feedbackPdfDownloaded,
    hasSessionData,
    sessionActive,
    showConfirm,
  ]);

  return {
    hasSessionData,
    resetConversationState,
    clearCurrentSessionData,
    clearSessionSelection,
    startSelectedProfileSession,
    confirmSessionTransition,
  };
}

export default useSessionTransition;
